import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, FormGroup, Input, Label, Row } from 'reactstrap';
import { feedbackDraftState } from './feedbackDraftState';

const TextQuestion = ({
  question,
  draftKey,
  updateEval,
  answerText,
  i,
  setIsTyping,
  checkIfStatusNeedsChangingToModify,
  questionaireDisabled,
}) => {
  const recoveryRef = useRef(feedbackDraftState.getDraft(draftKey));
  const draftRef = useRef(
    recoveryRef.current || {
      value: question.AnswerText || '',
      baseValue: question.AnswerText || '',
      version: 0,
      isDirty: false,
    },
  );
  const [text, setText] = useState(draftRef.current.value);
  const initialWrite = feedbackDraftState.getWrite(draftKey);
  const [saveState, setSaveState] = useState(
    initialWrite?.uncertain
      ? 'uncertain'
      : initialWrite?.pending
      ? 'waiting'
      : recoveryRef.current
      ? 'recovered'
      : 'idle',
  );
  const [conflictValue, setConflictValue] = useState(draftRef.current.conflictValue);
  const typingTimeoutRef = useRef(null);
  const isMountedRef = useRef(false);
  const isFocusedRef = useRef(false);
  const isWaitingRef = useRef(false);
  const handlersRef = useRef({});
  handlersRef.current = { updateEval, setIsTyping, checkIfStatusNeedsChangingToModify };
  const textLimit = 2500;

  useEffect(() => {
    isMountedRef.current = true;
    const unsubscribe = feedbackDraftState.subscribe(draftKey, (event) => {
      handlersRef.current.onWriteEvent(event);
    });
    if (recoveryRef.current || feedbackDraftState.getWrite(draftKey)) {
      handlersRef.current.setIsTyping(true);
    }
    return () => {
      isMountedRef.current = false;
      clearTimeout(typingTimeoutRef.current);
      unsubscribe();
      // Edits already live in bounded page memory; never discard their only copy.
      handlersRef.current.setIsTyping(false);
    };
  }, [draftKey]);

  useEffect(() => {
    if (feedbackDraftState.isAcknowledgement(question)) {
      return;
    }
    const incoming = question.AnswerText || '';
    if (incoming === draftRef.current.baseValue && draftRef.current.conflictValue === undefined) {
      return;
    }
    if (!draftRef.current.isDirty && !draftRef.current.hasAcknowledged && !isFocusedRef.current) {
      draftRef.current = { ...draftRef.current, value: incoming, baseValue: incoming };
      setText(incoming);
      return;
    }
    // Without server revisions a different readback may be newer OR stale. Preserve
    // both values and require a choice rather than keep displaying a false Saved.
    const next = { ...draftRef.current, version: draftRef.current.version + 1, isDirty: true, conflictValue: incoming };
    if (!feedbackDraftState.retain(draftKey, next)) {
      setSaveState('capacity');
      handlersRef.current.setIsTyping(true);
      return;
    }
    draftRef.current = next;
    clearTimeout(typingTimeoutRef.current);
    setConflictValue(incoming);
    setSaveState('conflict');
    handlersRef.current.setIsTyping(true);
  }, [question, draftKey]);

  const postAnswer = async (snapshot) => {
    if (!isMountedRef.current || snapshot.version !== draftRef.current.version || !draftRef.current.isDirty) {
      return;
    }
    isWaitingRef.current = Boolean(feedbackDraftState.getWrite(draftKey));
    setSaveState('saving');
    const result = await handlersRef.current.updateEval(snapshot.value, question.pk_EvaluationQuestion, 'text');
    if (!isMountedRef.current || snapshot.version !== draftRef.current.version || !draftRef.current.isDirty) {
      return;
    }
    if (result.waiting) {
      isWaitingRef.current = true;
      if (!feedbackDraftState.getWrite(draftKey)) {
        postAnswer(draftRef.current);
        return;
      }
      setSaveState(result.uncertain ? 'uncertain' : 'waiting');
    } else if (result.error) {
      setSaveState(result.capacity ? 'capacity' : result.uncertain ? 'uncertain' : 'error');
      handlersRef.current.setIsTyping(true);
    } else if (draftRef.current.conflictValue === undefined) {
      draftRef.current = { ...draftRef.current, baseValue: snapshot.value, isDirty: false, hasAcknowledged: true };
      feedbackDraftState.release(draftKey);
      setSaveState('saved');
      handlersRef.current.setIsTyping(false);
    }
  };

  const authorizeDraft = async (snapshot, previousDraft, isRetry = false) => {
    try {
      const isAllowed = await handlersRef.current.checkIfStatusNeedsChangingToModify();
      if (!isMountedRef.current || snapshot.version !== draftRef.current.version) {
        return;
      }
      if (!isAllowed) {
        draftRef.current = { ...previousDraft, version: snapshot.version + 1 };
        if (previousDraft.isDirty) {
          feedbackDraftState.retain(draftKey, draftRef.current);
        } else {
          feedbackDraftState.release(draftKey);
        }
        setText(previousDraft.value);
        setSaveState(previousDraft.isDirty ? 'error' : previousDraft.hasAcknowledged ? 'saved' : 'idle');
        handlersRef.current.setIsTyping(previousDraft.isDirty);
        return;
      }
      if (isRetry) {
        postAnswer(snapshot);
      } else {
        typingTimeoutRef.current = setTimeout(() => {
          postAnswer(snapshot);
        }, 2000);
      }
    } catch (err) {
      if (isMountedRef.current && snapshot.version === draftRef.current.version) {
        setSaveState('error');
      }
    }
  };
  const changeName = (event) => {
    const previousDraft = draftRef.current;
    const snapshot = {
      ...previousDraft,
      value: (event.target.value || '').substring(0, textLimit),
      version: previousDraft.version + 1,
      isDirty: true,
    };
    if (!feedbackDraftState.retain(draftKey, snapshot)) {
      setSaveState('capacity');
      setText(previousDraft.value);
      return;
    }
    draftRef.current = snapshot;
    setText(snapshot.value);
    handlersRef.current.setIsTyping(true);
    clearTimeout(typingTimeoutRef.current);
    if (snapshot.conflictValue !== undefined) {
      setSaveState('conflict');
      return;
    }
    if (feedbackDraftState.getWrite(draftKey)?.uncertain) {
      setSaveState('uncertain');
      return;
    }
    setSaveState('pending');
    authorizeDraft(snapshot, previousDraft);
  };
  const retry = () => {
    clearTimeout(typingTimeoutRef.current);
    if (draftRef.current.conflictValue !== undefined) {
      setSaveState('conflict');
      return;
    }
    if (feedbackDraftState.getWrite(draftKey)) {
      return;
    }
    setSaveState('pending');
    authorizeDraft(draftRef.current, draftRef.current, true);
  };
  const useReceivedText = () => {
    if (feedbackDraftState.getWrite(draftKey)) {
      return;
    }
    const value = draftRef.current.conflictValue;
    draftRef.current = { value, baseValue: value, version: draftRef.current.version + 1, isDirty: true };
    feedbackDraftState.retain(draftKey, draftRef.current);
    setText(value);
    setConflictValue(undefined);
    setSaveState('review');
    handlersRef.current.setIsTyping(true);
  };
  const saveMyText = () => {
    if (feedbackDraftState.getWrite(draftKey)) {
      return;
    }
    draftRef.current = {
      ...draftRef.current,
      baseValue: draftRef.current.conflictValue,
      conflictValue: undefined,
      version: draftRef.current.version + 1,
    };
    feedbackDraftState.retain(draftKey, draftRef.current);
    setConflictValue(undefined);
    retry();
  };
  handlersRef.current.onWriteEvent = (event) => {
    if (!isMountedRef.current) {
      return;
    }
    if (event.type === 'uncertain') {
      setSaveState('uncertain');
      handlersRef.current.setIsTyping(true);
      return;
    }
    if (event.result.error) {
      setSaveState(draftRef.current.conflictValue !== undefined ? 'conflict' : 'error');
      handlersRef.current.setIsTyping(true);
      return;
    }
    draftRef.current = { ...draftRef.current, baseValue: event.value, hasAcknowledged: true };
    if (draftRef.current.conflictValue !== undefined) {
      setSaveState('conflict');
      return;
    }
    if (draftRef.current.value === event.value) {
      draftRef.current = { ...draftRef.current, isDirty: false };
      feedbackDraftState.release(draftKey);
      setSaveState('saved');
      handlersRef.current.setIsTyping(false);
    } else if (isWaitingRef.current) {
      isWaitingRef.current = false;
      authorizeDraft(draftRef.current, draftRef.current, true);
    } else if (draftRef.current.isDirty) {
      setSaveState('recovered');
    }
  };

  return (
    <Row>
      <Col style={{ padding: 0 }} sm="12">
        <p style={{ fontWeight: 700 }}>
          {i + 1}. {question.EnableRequired && <span style={{ color: '#ff0000' }}>*</span>}
          {question.QuestionText}
        </p>
      </Col>
      <Col sm="12" style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'space-evenly', width: '100%' }}>
          <Form style={{ width: '100%' }}>
            <FormGroup>
              <Label
                for={`question_${question.pk_EvaluationQuestion}`}
                style={{
                  fontSize: 10,
                  fontWeight: 'bold',
                  color: text.length >= textLimit ? 'red' : text.length >= 2000 ? 'orange' : 'black',
                  margin: 0,
                  visibility: text.length >= 2000 ? 'visible' : 'hidden',
                }}
              >
                {textLimit - text.length || 0} character{text.length == 1 ? '' : 's'} remaining. . .
              </Label>
              <Input
                type="textarea"
                name="text"
                id={`question_${question.pk_EvaluationQuestion}`}
                value={text}
                onFocus={() => {
                  isFocusedRef.current = true;
                }}
                onBlur={() => {
                  isFocusedRef.current = false;
                  if (!draftRef.current.isDirty && !draftRef.current.hasAcknowledged) {
                    draftRef.current.value = question.AnswerText || '';
                    setText(draftRef.current.value);
                  }
                }}
                tabIndex={!questionaireDisabled ? 0 : -1}
                onChange={changeName}
                rows={5}
                maxLength={textLimit}
                style={{ border: text.length >= textLimit ? '1px solid red' : null }}
              />
              <div
                role={
                  ['error', 'uncertain', 'recovered', 'conflict', 'capacity', 'review'].includes(saveState)
                    ? 'alert'
                    : 'status'
                }
                style={{ minHeight: 48, fontSize: 12 }}
              >
                {saveState === 'pending' || saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : ''}

                {saveState === 'waiting' && 'Earlier save pending. Current text retained.'}
                {saveState === 'capacity' && 'Recovery limit reached. Save retained feedback before editing more.'}
                {['error', 'recovered', 'uncertain', 'review'].includes(saveState) && (
                  <>
                    {saveState === 'review'
                      ? 'Received text loaded; save to confirm. '
                      : saveState === 'recovered'
                      ? 'Unsaved feedback recovered. '
                      : saveState === 'uncertain'
                      ? 'Save outcome unknown. Draft retained. '
                      : 'Save not confirmed. '}
                    <Button
                      type="button"
                      color="link"
                      size="sm"
                      style={{ padding: 0 }}
                      disabled={questionaireDisabled || Boolean(feedbackDraftState.getWrite(draftKey))}
                      onClick={retry}
                    >
                      {saveState === 'review' ? 'Save' : 'Retry'}
                    </Button>
                    {saveState === 'uncertain' && (
                      <div>
                        Saving paused; keep this page open and copy your text. If confirmation does not arrive, contact
                        support.
                      </div>
                    )}
                  </>
                )}
                {saveState === 'conflict' && (
                  <>
                    Server text differs. Review before saving or submitting.
                    {feedbackDraftState.getWrite(draftKey)?.uncertain && (
                      <div>An earlier save is unconfirmed; saving is paused.</div>
                    )}
                    <details>
                      <summary>Received server text</summary>
                      <pre style={{ whiteSpace: 'pre-wrap' }}>{conflictValue || '(empty)'}</pre>
                    </details>
                    <Button
                      type="button"
                      color="link"
                      size="sm"
                      disabled={Boolean(feedbackDraftState.getWrite(draftKey))}
                      onClick={useReceivedText}
                    >
                      Use received text
                    </Button>
                    <Button
                      type="button"
                      color="link"
                      size="sm"
                      disabled={questionaireDisabled || Boolean(feedbackDraftState.getWrite(draftKey))}
                      onClick={saveMyText}
                    >
                      Save my text
                    </Button>
                  </>
                )}
              </div>
            </FormGroup>
          </Form>
        </div>
      </Col>
    </Row>
  );
};

export default TextQuestion;
